import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface ImageLightboxProps {
  imageUrl: string;
  alt: string;
  onClose: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ imageUrl, alt, onClose }) => {
  const lightboxRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  
  // Cerrar el lightbox al hacer clic fuera de la imagen
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === lightboxRef.current) {
      onClose();
    }
  };
  
  // Funciones de zoom
  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3)); // Limitar zoom a 3x
  };
  
  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5)); // No permitir menos de 0.5x
  };
  
  const resetZoom = () => {
    setScale(1);
  };
  
  // Manejar doble clic para zoom
  const handleDoubleClick = () => {
    if (scale > 1) {
      resetZoom();
    } else {
      zoomIn();
    }
  };
  
  // Manejar carga de imagen
  const handleImageLoad = () => {
    setIsLoading(false);
  };
  
  // Cerrar el lightbox al presionar Escape
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    // Manejar zoom con rueda del mouse
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        if (e.deltaY < 0) {
          zoomIn();
        } else {
          zoomOut();
        }
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    document.addEventListener('wheel', handleWheel, { passive: false });
    
    // Deshabilitar el scroll del body mientras el lightbox está abierto
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.removeEventListener('wheel', handleWheel);
      // Restaurar el scroll del body cuando se cierra el lightbox
      document.body.style.overflow = 'auto';
    };
  }, [onClose]);  // No incluir zoomIn/zoomOut en las dependencias para evitar recrear el efecto
  
  return (
    <div 
      ref={lightboxRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 backdrop-blur-sm"
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
    >
      {/* Overlay de carga */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          <span className="sr-only">{t('post.loading')}</span>
        </div>
      )}
      
      <div className="relative max-w-[95vw] max-h-[95vh] select-none">
        {/* Controles superiores */}
        <div className="absolute top-2 right-2 flex space-x-2 z-10">
          <button
            onClick={zoomIn}
            className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={t('post.zoomIn')}
            title={t('post.zoomIn')}
          >
            <MagnifyingGlassPlusIcon className="h-5 w-5" />
          </button>
          
          <button
            onClick={zoomOut}
            className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={t('post.zoomOut')}
            title={t('post.zoomOut')}
          >
            <MagnifyingGlassMinusIcon className="h-5 w-5" />
          </button>
          
          <button
            onClick={onClose}
            className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={t('post.closeImage')}
            title={t('post.closeImage')}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        {/* Contenedor de la imagen con transformación */}
        <div 
          className="overflow-auto max-w-[95vw] max-h-[95vh] flex items-center justify-center"
          style={{ 
            width: '95vw', 
            height: '95vh',
            cursor: scale > 1 ? 'move' : 'zoom-in'
          }}
        >
          <img 
            ref={imageRef}
            src={imageUrl} 
            alt={alt}
            className="object-contain rounded-lg transition-transform duration-200"
            style={{ 
              transform: `scale(${scale})`,
              transformOrigin: 'center center',
              maxWidth: '100%',
              maxHeight: '100%'
            }}
            onLoad={handleImageLoad}
            onDoubleClick={handleDoubleClick}
          />
        </div>
        
        {/* Indicador de escala */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
          {Math.round(scale * 100)}%
        </div>
      </div>
    </div>
  );
};

export default ImageLightbox;
